<template>
  <div v-if="visible"
       class="ship-sheet-ov"
       @click.self="later">
    <div ref="sheet"
         class="ship-sheet nudge-sheet"
         role="dialog"
         aria-modal="true"
         aria-labelledby="nudge-h"
         tabindex="-1">
      <div class="ship-sheet-body">
        <div class="nudge">
          <h4 id="nudge-h"
              class="font-weight-bold text-color-2 nudge-head">{{ title }}</h4>
          <div class="mp-row">
            <PageAvatar :page="samplePage"
                        size="sm" />
            <div class="mp-bubble">
              <div class="mp-text">{{ $t("messenger.ship.examples.sample_bubble") }}</div>
              <span class="mp-btn">{{ $t("messenger.ship.examples.track_button") }}</span>
            </div>
          </div>
          <p class="ship-panel-bold">{{ $t("messenger.ship.examples.auto_bold") }}</p>
          <ul class="ship-panel-list">
            <li>{{ $t("messenger.ship.examples.benefit_1") }}</li>
            <li>{{ $t("messenger.ship.examples.benefit_2") }}</li>
            <li>{{ $t("messenger.ship.examples.benefit_3") }}</li>
          </ul>
          <p class="nudge-free">{{ $t("messenger.ship.nudge.free") }}</p>
          <div class="nudge-foot">
            <Button variant="primary"
                    size="lg"
                    :loading="connecting"
                    @click="connect">
              <i v-if="!connecting"
                 class="fab fa-facebook mr-1"></i>{{ $t("messenger.ship.connect") }}
            </Button>
            <button type="button"
                    class="btn btn-outline-primary btn-lg"
                    @click="later">{{ $t("messenger.ship.nudge.later") }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Button from "@/components/core/Button";
import PageAvatar from "@/components/derived/messenger/PageAvatar";
import { isPageConnected } from "@/store/modules/messenger";
import { Toast } from "@/helpers/toastr";

const NUDGE_DELAY_MS = 1000;
const SKIP_ROUTES = ["Seller-Settings-Shipping", "Seller-Settings-Shipping-Shop"];

// Once-a-day nudge (mounted in the seller layout) when no Page is connected. Never on
// ตั้งค่า › การจัดส่ง or the connect return. No X: Esc and the scrim mean ไว้ทีหลัง.
export default {
  name: "NudgePopup",
  components: { Button, PageAvatar },

  data() {
    return {
      visible: false,
      connecting: false,
      timer: null,
    };
  },

  computed: {
    title() {
      return this.$t("messenger.ship.nudge.a_title");
    },
    samplePage() {
      return { pageId: "sample", name: this.$t("messenger.ship.examples.sample_page"), pictureUrl: null };
    },
  },

  created() {
    if (this.suppressed()) return;
    this.timer = setTimeout(this.maybeShow, NUDGE_DELAY_MS);
  },

  beforeDestroy() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    document.body.classList.remove("--sheet-open");
    document.removeEventListener("keydown", this.onKey);
  },

  methods: {
    suppressed() {
      const name = this.$route && this.$route.name;
      const query = (this.$route && this.$route.query) || {};
      if (query.claim != null || query.connected != null || query.error != null) return true;
      return SKIP_ROUTES.indexOf(name) !== -1;
    },

    seller() {
      const authData = this.$store.state.user && this.$store.state.user.authData;
      const user = authData && authData.user;
      return user && user.id ? { id: String(user.id), role: user.role } : null;
    },

    nudgeDay() {
      const now = new Date();
      return now.getFullYear() + "-" + (now.getMonth() + 1) + "-" + now.getDate();
    },

    maybeShow() {
      this.timer = null;
      if (this._isDestroyed || this.suppressed()) return;
      const seller = this.seller();
      if (!seller || seller.role !== "seller") return;
      const key = "shipNudge:" + seller.id;
      const today = this.nudgeDay();
      let stamped = null;
      try {
        stamped = window.localStorage.getItem(key);
      } catch (e) {
        stamped = null;
      }
      if (stamped === today) return;
      this.$store.dispatch("messenger/fetchPages", {
        callback: (ok, pages) => {
          if (this._isDestroyed || this.suppressed()) return;
          if (!ok) return;
          const connected = (pages || []).filter(isPageConnected);
          if (!connected.length) this.show(key, today);
        },
      });
    },

    show(key, today) {
      try {
        window.localStorage.setItem(key, today);
      } catch (e) {
        // storage blocked: the nudge may show again next visit
      }
      this.visible = true;
      document.body.classList.add("--sheet-open");
      document.addEventListener("keydown", this.onKey);
      this.$nextTick(() => {
        if (this.$refs.sheet) this.$refs.sheet.focus();
      });
    },

    onKey(event) {
      if (event && event.key === "Escape") this.later();
    },

    later() {
      this.visible = false;
      document.body.classList.remove("--sheet-open");
      document.removeEventListener("keydown", this.onKey);
    },

    connect() {
      if (this.connecting) return;
      this.connecting = true;
      const redirectUrl = window.location.origin + window.location.pathname + "#/settings/shipping";
      this.$store.dispatch("messenger/connectUrl", {
        redirectUrl,
        callback: (ok, url) => {
          if (ok && url) {
            window.location.assign(url); // stays "connecting" while the browser leaves for Facebook
            return;
          }
          this.connecting = false;
          Toast(this.$t("messenger.pages.connect_failed"), "danger");
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.ship-sheet .btn {
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.ship-sheet-ov {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1050;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

@media (min-width: 768px) {
  .ship-sheet-ov {
    align-items: center;
    padding: 2rem;
  }
}

.ship-sheet {
  position: relative;
  width: 100%;
  max-width: 720px;
  max-height: 88vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 16px 16px 0 0;
}

.nudge-sheet {
  max-width: 600px;
}

@media (min-width: 768px) {
  .ship-sheet {
    border-radius: 16px;
  }
}

.ship-sheet-body {
  padding: 1.25rem;
}

.nudge {
  max-width: 600px;
  margin: 0 auto;
}

.nudge-head {
  margin: 0 0 1rem;
}

.nudge .mp-row,
.nudge .ship-panel-bold,
.nudge .ship-panel-list {
  margin-bottom: 0.9rem;
}

.mp-row {
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
}

.mp-bubble {
  background: #f0f0f0;
  border-radius: 18px;
  max-width: 290px;
  overflow: hidden;
  color: #1c1e21;
  min-width: 0;
}

.mp-text {
  padding: 0.6rem 0.9rem;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.9375rem;
  line-height: 1.45;
}

.mp-btn {
  display: block;
  border-top: 1px solid #dcdcdc;
  padding: 0.55rem 0.9rem;
  text-align: center;
  color: #1c75bb;
  font-weight: 700;
  font-size: 0.9375rem;
}

.ship-panel-bold {
  font-weight: 700;
  color: #333;
  margin: 0.8rem 0 0.35rem;
  font-size: 0.9375rem;
}

.ship-panel-list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  color: #646464;
  font-size: 0.9375rem;
}

.nudge-free {
  font-weight: 700;
  color: #333;
  font-size: 1rem;
  margin: 0;
}

.nudge-foot {
  position: sticky;
  bottom: 0;
  margin: 1.1rem -1.25rem -1.25rem;
  padding: 0.9rem 1.25rem 1.25rem;
  background: #fff;
  border-top: 1px solid #eee;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;

  .btn {
    width: 100%;
  }
}

body:not(.--dark) .mp-btn {
  color: #155f99;
}

body.--dark {
  .ship-sheet {
    background: #373737;
  }

  .mp-bubble {
    background: #4b4b4b;
    color: lightgray;
  }

  .mp-btn {
    color: #8cc8f5;
    border-top-color: #666;
  }

  .ship-panel-bold,
  .nudge-free {
    color: lightgray;
  }

  .ship-panel-list {
    color: #bbb;
  }

  .nudge-foot {
    background: #373737;
    border-top-color: #555;
  }

  .ship-sheet .btn-outline-primary {
    color: #8cc8f5;
    border-color: #8cc8f5;
  }

  .ship-sheet .btn-outline-primary:hover:not(:disabled) {
    color: #fff;
  }
}
</style>
